import { Button, EmptyState, Table, useDrawer } from '@iveco-ti/iveco-ds'
import { Plus } from 'lucide-react'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'

import { ExportDropdown } from '@/common/components/ExportDropdown'
import { FilterButton } from '@/common/components/FilterButton'
import { OrderByDropdown } from '@/common/components/OrderByDropdown'
import { useDownloadExport } from '@/common/hooks/useDownloadExport'
import { DrawerEditUser } from './components/DrawerEditUser'
import { DrawerFilterUsers } from './components/DrawerFilterUsers'
import { DrawerNewUser } from './components/DrawerNewUser'
import { DrawerViewUser } from './components/DrawerViewUser'
import { TableRowSkeleton } from './components/TableRowSkeleton'
import { UserTableRow } from './components/UserTableRow'
import { usePaginateUsers } from './hooks/usePaginateUsers'

export default function Users() {
  const { t } = useTranslation(['registers', 'common'])
  const {
    users,
    usersPaginated,
    isLoadingUsers,
    usersBadges,
    usersOrderBy,
    usersPerPage,
    filtersQty,
    handleUsersSearch,
    handleChangePage,
    handleChangeOrder,
  } = usePaginateUsers()
  const { downloadExport, isDownloadingExport } = useDownloadExport({
    route: '/user/report',
  })
  const drawerFilterUsers = useDrawer('drawer-filter-users')
  const drawerNewUser = useDrawer('drawer-new-user')

  const ORDER_BY_OPTIONS = [
    {
      label: t('registers:users.table.order_dropdown.fields.name.label'),
      field: 'UserPreference.displayName',
    },
    {
      label: t('registers:users.table.order_dropdown.fields.profile.label'),
      field: 'userType',
    },
  ]

  const TABLE_COLUMNS = [
    {
      label: t('registers:users.table.table_head.column_first.label'),
      order: 'Person.name',
    },
    {
      label: t('registers:users.table.table_head.column_second.label'),
      order: 'email',
    },
    {
      label: t('registers:users.table.table_head.column_third.label'),
      order: 'userType',
      badge: usersBadges?.userType,
    },
    {
      label: t('registers:users.table.table_head.column_fourth.label'),
      end: true,
    },
  ]

  return (
    <>
      <div className="overflow-y-auto h-full shadow-sm rounded-xl bg-white">
        <HeadProvider>
          <Title>{t('registers:users.browser.title')}</Title>
        </HeadProvider>

        <Table.Root>
          <Table.Search
            searchName="search"
            searchPlaceholder={t(
              'common:authenticated_layout.search_input.placeholder',
            )}
            onSearch={handleUsersSearch}
          >
            <OrderByDropdown
              selected={usersOrderBy}
              onChangeOrder={handleChangeOrder}
              options={ORDER_BY_OPTIONS}
            />

            <ExportDropdown
              onExportCSV={() => downloadExport({}, 'csv')}
              onExportXLSX={() => downloadExport({}, 'xlsx')}
              isExporting={isDownloadingExport}
            />

            <FilterButton open={drawerFilterUsers.open} filters={filtersQty} />

            <Button.Root onClick={drawerNewUser.open}>
              <Button.Icon icon={Plus} />
            </Button.Root>
          </Table.Search>

          {isLoadingUsers && <TableRowSkeleton />}
          {!users.length && !isLoadingUsers && (
            <EmptyState
              title={t('registers:users.empty_state.title')}
              description={t('registers:users.empty_state.description')}
            />
          )}

          <Table.Content>
            {!isLoadingUsers && !!users.length && (
              <>
                <Table.Head
                  hideCheckbox
                  cols={TABLE_COLUMNS}
                  onOrderBy={(order) => handleChangeOrder(order, 'asc')}
                />

                <Table.Body>
                  {users.map((user) => (
                    <UserTableRow key={user.id} user={user} />
                  ))}
                </Table.Body>
              </>
            )}
          </Table.Content>

          <Table.Footer
            page={usersPaginated?.currentPage || 1}
            totalItems={usersPaginated?.totalItems || 0}
            totalItemsLabel={t(
              'registers:users.table.table_footer.total_registers.label',
            )}
            totalPages={usersPaginated?.totalPages || 1}
            ofText={t('common:components.table_footer.pagination.label')}
            onChangePage={handleChangePage}
            itemsPerPage={usersPerPage}
          />
        </Table.Root>
      </div>

      <DrawerFilterUsers />
      <DrawerNewUser />
      <DrawerEditUser />
      <DrawerViewUser />
    </>
  )
}
